import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { camelCase } from 'lodash';
import { Option } from '../../core/components/Fields/AutoCompleteField';
import { fetchLookupOptions } from '../../document/components/useLookup';
import { SIEMENS_DEFAULT_COMPONENT_DESCRIPTIONS } from './defaultComponentDescriptions';

/**
 * Pre-fetches the 5 default "often used" component options for the current rail system partition.
 * Each description is searched individually (targetField='description', size=1) and the first
 * result is taken. Results are cleared and re-fetched whenever partitionId changes.
 *
 * Each option is returned with:
 *   - value = the actual field value (via lookupFieldName), so useForceSelection can validate it
 *   - label = the human-readable description, so renderArticleIdOption shows a readable primary text
 */
export const useDefaultComponents = ({
    lookupDefinitionId,
    lookupFieldName,
    partitionId,
}: {
    lookupDefinitionId: string;
    lookupFieldName: string;
    partitionId: string | undefined;
}) => {
    const [defaultOptions, setDefaultOptions] = useState<Option[]>([]);
    const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
    const client = useApolloClient();

    useEffect(() => {
        if (!partitionId || !lookupDefinitionId) {
            setDefaultOptions([]);
            return;
        }

        setIsLoadingDefaults(true);

        const fetchByDescription = fetchLookupOptions({
            client,
            lookupDefinitionId,
            lookupFieldName: 'description',
            partitionId,
        });

        Promise.allSettled(SIEMENS_DEFAULT_COMPONENT_DESCRIPTIONS.map((desc) => fetchByDescription(desc, 0, 1)))
            .then((results) => {
                const seenValues = new Set<string>();
                const options = results
                    .map((result, i) => {
                        // Handle both fulfilled and rejected promises
                        if (result.status === 'rejected') {
                            return null;
                        }

                        const opt = result.value[0];
                        // Discard results that don't have a valid option
                        if (!opt) return null;

                        return opt;
                    })
                    .filter((opt): opt is NonNullable<typeof opt> => opt !== null)
                    .map((opt) => ({
                        ...opt,
                        // fetchLookupOptions set value=node.description because we passed lookupFieldName='description'
                        // as the targetField. Override with the actual partition-specific field value so that
                        // useForceSelection can validate the selected option correctly.
                        value: opt[camelCase(lookupFieldName)],
                    }))
                    // Deduplicate by value in case two descriptions map to the same component
                    .filter((opt) => {
                        if (seenValues.has(opt.value)) return false;
                        seenValues.add(opt.value);
                        return true;
                    });
                setDefaultOptions(options);
            })
            .finally(() => setIsLoadingDefaults(false));
    }, [partitionId, lookupDefinitionId, lookupFieldName, client]);

    return { defaultOptions, isLoadingDefaults };
};
